// package imports
import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import { KitButton } from 'components/common/cms-kit';
import { Box } from '@mui/material';
import { useIsFetching } from '@tanstack/react-query';
import { Theme as MuiTheme } from '@mui/material/styles';

// local imports
import { default as SavedSnack } from '../AlertSnack';
import {
  useObservationEventInstanceList,
  useUpdateObservationEventSummaryNotes,
} from '../../hooks/ObservationEventHooks';
import PleaseWait from 'components/common/PleaseWait';
import { logClientException } from 'appinsights/clientAppInsights';
import { BadRequestError } from 'services/apiWrapper';
import { ObservationEvent } from 'types/ObservationTypes';
import { useSessionStorage } from 'services/hooks/useSessionStorage';
import NoteSection from './NoteSection';

const defaultErrorMessage =
  'An unexpected error has occured. Please try again.';
const defaultToastAutoHide = 2500;

const EventSummaryNotes: React.FC<{ event: ObservationEvent }> = ({
  event,
}) => {
  const theme = useTheme() as MuiTheme;
  const [snackSave, setSnackSave] = useState<{
    message: string | null;
    severity: 'success' | 'error' | 'info' | 'warning';
    autoHideDuration: number;
  }>({
    message: null,
    severity: 'success',
    autoHideDuration: defaultToastAutoHide,
  });

  const eventInstanceListQuery = useObservationEventInstanceList(
    event?.id || '',
  );

  useEffect(() => {
    if (eventInstanceListQuery.isStale) {
      eventInstanceListQuery.refetch();
    }
  }, [eventInstanceListQuery.isStale, eventInstanceListQuery.refetch]);

  const [eventState, setEventState] = useSessionStorage(
    `eventSummaryNotes_${event?.id}`,
    {
      areas_strength: '',
      areas_growth: '',
      general_notes: '',
      questions: '',
      next_steps: '',
      is_dirty: false,
    },
  );

  useEffect(() => {
    if (event?.event_summary_notes) {
      setEventState({
        areas_strength: event.event_summary_notes.AreasOfStrength?.note || '',
        areas_growth: event.event_summary_notes.AreasOfGrowth?.note || '',
        general_notes: event.event_summary_notes.General?.note || '',
        questions: event.event_summary_notes.Questions?.note || '',
        next_steps: event.event_summary_notes.NextSteps?.note || '',
        is_dirty: false,
      });
    }
  }, [event]);

  const afterObservationMutate = (data: ObservationEvent) => {
    setSnackSave({
      message: 'Event Notes Saved',
      severity: 'success',
      autoHideDuration: defaultToastAutoHide,
    });
    setEventState({
      areas_strength: data.event_summary_notes?.AreasOfStrength?.note || '',
      areas_growth: data.event_summary_notes?.AreasOfGrowth?.note || '',
      general_notes: data.event_summary_notes?.General?.note || '',
      questions: data.event_summary_notes?.Questions?.note || '',
      next_steps: data.event_summary_notes?.NextSteps?.note || '',
      is_dirty: false,
    });
  };

  const afterObservationMutateFail = (error) => {
    logClientException(error);
    let message = defaultErrorMessage;
    if (error instanceof BadRequestError) {
      message = error.formatMessage();
    }
    setSnackSave({
      message: message,
      severity: 'warning',
      autoHideDuration: defaultToastAutoHide,
    });

    updateEvent.reset();
  };

  const updateEvent = useUpdateObservationEventSummaryNotes(
    event?.id || '',
    afterObservationMutate,
    afterObservationMutateFail,
  );

  const isFetchingCount = useIsFetching();

  return (
    <Box sx={{ margin: '1rem 0' }}>
      <NoteSection
        label="General"
        value={eventState.general_notes}
        onChange={(v) =>
          setEventState({
            ...eventState,
            general_notes: v,
            is_dirty: true,
          })
        }
        placeholder="Enter general notes"
        id="general-notes"
      />

      <NoteSection
        label="Areas of Strength"
        value={eventState.areas_strength}
        onChange={(v) => {
          setEventState({
            ...eventState,
            areas_strength: v,
            is_dirty: true,
          });
        }}
        placeholder="Enter areas of strength"
        id="areas-strength"
      />

      <NoteSection
        label="Areas of Growth"
        value={eventState.areas_growth}
        onChange={(v) =>
          setEventState({
            ...eventState,
            areas_growth: v,
            is_dirty: true,
          })
        }
        placeholder="Enter areas of growth"
        id="areas-growth"
      />

      <NoteSection
        label="Questions"
        value={eventState.questions}
        onChange={(v) =>
          setEventState({
            ...eventState,
            questions: v,
            is_dirty: true,
          })
        }
        placeholder="Enter questions"
        id="questions"
      />

      <NoteSection
        label="Next Steps"
        value={eventState.next_steps}
        onChange={(v) =>
          setEventState({
            ...eventState,
            next_steps: v,
            is_dirty: true,
          })
        }
        required
        placeholder="Enter next steps"
        id="next-steps"
      />

      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        <KitButton
          size="sm"
          style={{
            backgroundColor: theme.palette.gold.main,
          }}
          disabled={!eventState.is_dirty}
          onClick={async () => {
            await updateEvent.mutateAsync({
              id: event?.id || '',
              areas_of_growth: eventState.areas_growth,
              areas_of_strength: eventState.areas_strength,
              general: eventState.general_notes,
              questions: eventState.questions,
              next_steps: eventState.next_steps,
            });
          }}
        >
          Save Notes
        </KitButton>
      </Box>

      <SavedSnack
        message={snackSave.message}
        openSnack={snackSave.message === null ? false : true}
        onClose={() => {
          setSnackSave({
            message: null,
            severity: 'success',
            autoHideDuration: defaultToastAutoHide,
          });
        }}
        severity={snackSave.severity}
        autoHideDuration={snackSave.autoHideDuration}
      />

      <PleaseWait isLoading={updateEvent.isPending || isFetchingCount > 0} />
    </Box>
  );
};

export default EventSummaryNotes;
